import { FC } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import { BlockOutlined, Logout, SwapHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface StoreOption {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
}

interface SubscriptionExpiredModalProps {
  open: boolean;
  stores: StoreOption[];
  currentStoreId: string | null;
  onLogout: () => void;
  onSwitchStore: (storeId: string) => void;
}

export const SubscriptionExpiredModal: FC<SubscriptionExpiredModalProps> = ({
  open,
  stores,
  currentStoreId,
  onLogout,
  onSwitchStore,
}) => {
  const { t } = useTranslation('common');

  const otherStores = stores.filter(
    (s) => s.id !== currentStoreId && s.isActive
  );

  return (
    <Dialog
      open={open}
      fullScreen
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          background: '#f5f5f5',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 480,
            width: '100%',
            bgcolor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            p: { xs: 3, sm: 5 },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <BlockOutlined sx={{ fontSize: 36, color: '#ffffff' }} />
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.87)', mb: 1 }}
          >
            {t('subscription.expired_title')}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'rgba(0,0,0,0.6)', mb: 4 }}
          >
            {t('subscription.expired_message')}
          </Typography>

          <Stack spacing={2}>
            {otherStores.length > 0 && (
              <>
                <Divider>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(0,0,0,0.4)' }}
                  >
                    {t('storeSelector.switch_to_another')}
                  </Typography>
                </Divider>

                {otherStores.map((store) => (
                  <Button
                    key={store.id}
                    variant="outlined"
                    startIcon={<SwapHoriz />}
                    onClick={() => onSwitchStore(store.id)}
                    sx={{
                      color: '#1976d2',
                      borderColor: 'rgba(25,118,210,0.3)',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#1976d2',
                        bgcolor: 'rgba(25,118,210,0.08)',
                      },
                    }}
                  >
                    {store.name}
                  </Button>
                ))}

                <Divider />
              </>
            )}

            <Button
              variant="contained"
              size="large"
              startIcon={<Logout />}
              onClick={onLogout}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                py: 1.2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {t('navigation.logout')}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
